import React, { useState, useEffect } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import { getEmptyMessage, getSuccessMessage, getErrorMessage } from '../../utils/MessageUtils';
import { processError } from '../../services/ServiceUtils';
import { getLaunchRadar, prepareLaunch, setLaunchAlerts, setLaunchEnabled } from '../../services/StrategiesService';

// LAUNCHPAD — radar de listagens novas na Binance + "Preparar lançamento" (escolha HUMANA). Read-only até
// o usuário clicar em Preparar (que cria uma estratégia [Launch] PAUSADA, revisável na tela de Estratégias).
// Alto risco por natureza — a tela deixa isso explícito. Ver backend utils/launchpad + project_backlog_launchpad.
function Launchpad() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [budget, setBudget] = useState(20);
    const [preset, setPreset] = useState('MODERATE');
    const [busy, setBusy] = useState('');
    const [notification, setNotification] = useState(getEmptyMessage());

    // Espelha strategyTemplates.TEMPLATES(LAUNCH).presets no backend — inclui timeStopMinutes
    // (saída por tempo: fecha o restante da posição pós-estreia se não bater alvo nem stop).
    const PRESETS = {
        CONSERVATIVE: { usdtAmount: 10, interval: '15m', rsiBuy: 35, rsiSell: 70, stopLossPct: 10, timeStopMinutes: 240 },
        MODERATE:     { usdtAmount: 20, interval: '5m',  rsiBuy: 40, rsiSell: 75, stopLossPct: 12, timeStopMinutes: 360 },
        AGGRESSIVE:   { usdtAmount: 50, interval: '5m',  rsiBuy: 45, rsiSell: 80, stopLossPct: 18, timeStopMinutes: 720 },
    };

    function load() {
        setLoading(true);
        getLaunchRadar()
            .then(d => { setData(d); if (d && d.listings) { /* mantém budget do usuário */ } })
            .catch(err => setNotification(getErrorMessage(processError(err))))
            .finally(() => setLoading(false));
    }

    useEffect(() => { load(); }, []);

    function onToggleEnabled(e) {
        const enabled = e.target.checked;
        setLaunchEnabled(enabled)
            .then(() => { setData(d => ({ ...d, enabled })); setNotification(getSuccessMessage(enabled ? 'Launchpad LIGADO.' : 'Launchpad desligado.')); })
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }

    function onToggleAlerts(e) {
        const enabled = e.target.checked;
        setLaunchAlerts(enabled)
            .then(() => { setData(d => ({ ...d, alertsEnabled: enabled })); setNotification(getSuccessMessage(enabled ? 'Alertas de lançamento LIGADOS.' : 'Alertas de lançamento desligados.')); })
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }

    function onPrepare(pair) {
        setBusy(pair);
        prepareLaunch({ symbol: pair, ...PRESETS[preset], usdtAmount: budget })
            .then(r => {
                if (r && r.existing) setNotification(getSuccessMessage(`Já existia uma estratégia para ${pair}.`));
                else setNotification(getSuccessMessage(`Estratégia [Launch] ${pair} criada PAUSADA. Revise e ative em Estratégias.`));
                load();
            })
            .catch(err => setNotification(getErrorMessage(processError(err))))
            .finally(() => setBusy(''));
    }

    const enabled = !!(data && data.enabled);
    const listings = (data && data.listings) || [];

    return (
        <React.Fragment>
            <Menu />
            <main className='content'>
                <div className='d-flex justify-content-between align-items-center py-4'>
                    <div>
                        <h2 className='h4'>🚀 Lançamentos (Launchpad)</h2>
                        <p className='text-muted mb-0' style={{ fontSize: 14 }}>
                            Moedas recém-anunciadas na Binance. Você escolhe quais preparar — nada opera sozinho.
                        </p>
                    </div>
                    <button className='btn btn-outline-primary btn-sm' onClick={load} disabled={loading}>
                        {loading ? 'Atualizando…' : '↻ Atualizar'}
                    </button>
                </div>

                {/* Master: liga/desliga a feature (participa da varredura). */}
                <div className='card border-0 shadow mb-4'>
                    <div className='card-body py-3 d-flex align-items-center justify-content-between'>
                        <div className='form-check form-switch mb-0'>
                            <input className='form-check-input' type='checkbox' id='launchEnabled'
                                checked={enabled} onChange={onToggleEnabled} />
                            <label className='form-check-label fw-bold' htmlFor='launchEnabled' style={{ fontSize: 14 }}>
                                Habilitar Launchpad
                            </label>
                            <div className='text-muted' style={{ fontSize: 12 }}>
                                Quando ligado, o bot varre listagens novas periodicamente. Desligado, não varre nem notifica.
                            </div>
                        </div>
                    </div>
                </div>

                {!enabled && (
                    <div className='card border-0 shadow'><div className='card-body text-muted' style={{ fontSize: 14 }}>
                        Launchpad desligado. Ligue acima para ver o radar de moedas recém-listadas e preparar estratégias de estreia.
                    </div></div>
                )}

                {enabled && <>
                {/* Aviso de risco — permanente */}
                <div className='alert alert-warning py-2' style={{ fontSize: 13 }}>
                    ⚠️ <strong>Alto risco.</strong> Moeda nova não tem histórico e o 1º minuto costuma ser topo local
                    (você vira liquidez de saída). Toda estratégia nasce <strong>PAUSADA</strong> com orçamento fixo,
                    hard stop e take-profit — revise antes de armar. É ferramenta de disciplina, não máquina de lucro.
                </div>

                {/* Controles: alerta + preset + orçamento */}
                <div className='card border-0 shadow mb-4'>
                    <div className='card-body py-3 d-flex flex-wrap align-items-end gap-4'>
                        <div className='form-check form-switch'>
                            <input className='form-check-input' type='checkbox' id='launchAlerts'
                                checked={!!(data && data.alertsEnabled)} onChange={onToggleAlerts} />
                            <label className='form-check-label' htmlFor='launchAlerts' style={{ fontSize: 14 }}>
                                Avisar no Telegram quando surgir listagem nova
                            </label>
                        </div>
                        <div>
                            <label className='form-label mb-1' style={{ fontSize: 12 }}>Perfil de risco</label>
                            <select className='form-select form-select-sm' value={preset}
                                onChange={e => { setPreset(e.target.value); setBudget(PRESETS[e.target.value].usdtAmount); }} style={{ width: 160 }}>
                                <option value='CONSERVATIVE'>Conservador</option>
                                <option value='MODERATE'>Moderado</option>
                                <option value='AGGRESSIVE'>Agressivo</option>
                            </select>
                        </div>
                        <div>
                            <label className='form-label mb-1' style={{ fontSize: 12 }}>Orçamento por lançamento (USDT)</label>
                            <input type='number' min='1' className='form-control form-control-sm' style={{ width: 140 }}
                                value={budget} onChange={e => setBudget(parseFloat(e.target.value) || 0)} />
                        </div>
                    </div>
                </div>

                {/* Radar */}
                {loading && <p className='text-muted'>Carregando radar…</p>}
                {!loading && !listings.length && (
                    <div className='card border-0 shadow'><div className='card-body text-muted' style={{ fontSize: 14 }}>
                        {(data && data.note) || 'Nenhuma listagem de spot recente no feed agora.'}
                    </div></div>
                )}
                {!loading && listings.length > 0 && (
                    <div className='card border-0 shadow'>
                        <div className='table-responsive'>
                            <table className='table table-hover align-middle mb-0'>
                                <thead><tr>
                                    <th>Moeda</th><th>Anúncio</th><th>Quando</th><th>Status</th><th className='text-end'>Ação</th>
                                </tr></thead>
                                <tbody>
                                    {listings.map((l, i) => (
                                        <tr key={i}>
                                            <td className='fw-bold'>{l.pair}</td>
                                            <td style={{ fontSize: 13, maxWidth: 420 }} className='text-truncate' title={l.title}>{l.title}</td>
                                            <td style={{ fontSize: 12 }}>{l.releaseDate ? new Date(Number(l.releaseDate)).toLocaleString('pt-BR') : '—'}</td>
                                            <td style={{ fontSize: 12 }}>
                                                {l.alreadyPrepared
                                                    ? <span className='badge bg-success'>Preparada</span>
                                                    : l.trading
                                                        ? <span className='badge bg-info'>Negociando</span>
                                                        : <span className='badge bg-secondary'>Ainda não listada</span>}
                                            </td>
                                            <td className='text-end'>
                                                {l.alreadyPrepared
                                                    ? <a className='btn btn-sm btn-outline-secondary' href='/strategies'>Ver em Estratégias</a>
                                                    : <button className='btn btn-sm btn-primary' disabled={busy === l.pair}
                                                        onClick={() => onPrepare(l.pair)}>
                                                        {busy === l.pair ? 'Preparando…' : 'Preparar (pausada)'}
                                                    </button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                </>}

                <Footer />
            </main>
            <Toast text={notification.text} type={notification.type} />
        </React.Fragment>
    );
}

export default Launchpad;
